import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { usePersistentState } from "../Common/usePersistentFilters";
import {
 Badge,
 Button,
 Card,
 Col,
 Form,
 Modal,
 ProgressBar,
 Row,
 Spinner,
 Table,
} from "react-bootstrap";
import {
 Bar,
 BarChart,
 CartesianGrid,
 Cell,
 Legend,
 Pie,
 PieChart,
 ResponsiveContainer,
 Tooltip,
 XAxis,
 YAxis,
} from "recharts";
import * as XLSX from "xlsx";
import api from "../../../api";

// helpers 

const fmt = (v) =>
 `₹ ${Number(v || 0).toLocaleString("en-IN", {
 minimumFractionDigits: 2,
 maximumFractionDigits: 2,
 })}`;

const fmtCr = (v) => {
 const n = Number(v || 0);
 if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
 if (n >= 100000) return `₹${(n / 100000).toFixed(2)} L`;
 if (n >= 1000) return `₹${(n / 1000).toFixed(1)} K`;
 return fmt(v);
};

const fmtPct = (v) => `${Number(v || 0).toFixed(1)}%`;

const fmtDate = (value) => {
 if (!value) return "—";
 const date = new Date(value);
 if (Number.isNaN(date.getTime())) return "—";

 const day = String(date.getDate()).padStart(2, "0");
 const month = String(date.getMonth() + 1).padStart(2, "0");
 return `${day}/${month}/${date.getFullYear()}`;
};

const pct = (actual, planned) =>
 !planned ? 0 : Math.min(100, Math.round((actual / planned) * 100));

const statusVariant = (p) =>
 p >= 90 ? "success" : p >= 60 ? "warning" : "danger";

const billingBadge = (status) => {
 const map = {
 Draft: "secondary",
 Submitted: "warning",
 Approved: "info",
 Rejected: "danger",
 Paid: "success",
 "Partially Paid": "primary",
 };
 return map[status] || "secondary";
};

const BRAND_COLORS = [
 "#0078D7", "#28A745", "#F47920", "#DC3545",
 "#FFC107", "#17A2B8", "#6F42C1", "#20C997",
];

// sub-components 

const StatCard = ({ icon, label, value, color, sub }) => (
 <div
 className="card border-0 shadow-sm h-100"
 style={{ borderLeft: `4px solid ${color}` }}
 >
 <div className="card-body d-flex align-items-center p-3 gap-3">
 <div
 className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
 style={{ width: 44, height: 44, background: `${color}18` }}
 >
 <i className={`fas ${icon}`} style={{ color, fontSize: 16 }} />
 </div>
 <div className="overflow-hidden">
 <p className="mb-0 text-muted text-truncate" style={{ fontSize: 10 }}>
 {label}
 </p>
 <h5 className="mb-0 fw-bold text-truncate">{value}</h5>
 {sub && <small className="text-muted">{sub}</small>}
 </div>
 </div>
 </div>
);

const SectionHeader = ({ icon, title, action }) => (
 <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
 <h6 className="fw-bold mb-0">
 <i className={`fas ${icon} text-primary me-2`} />
 {title}
 </h6>
 {action}
 </div>
);

// Recovery Statement Modal 

const RecoveryStatementModal = ({ show, onHide, projectId, projects, workOrders }) => {
 const [filters, setFilters] = useState({ project: projectId || "", workOrder: "", status: "" });
 const [billings, setBillings] = useState([]);
 const [loading, setLoading] = useState(false);

 // Sync project filter whenever modal opens or parent project changes
 useEffect(() => {
 if (!show) return;
 setFilters({ project: projectId || "", workOrder: "", status: "" });
 }, [show, projectId]);

 // Work orders filtered by currently selected project
 const filteredWOs = useMemo(
 () =>
 workOrders.filter((w) =>
 !filters.project ||
 String(w.project?._id || w.project || "") === String(filters.project)
 ),
 [workOrders, filters.project]
 );

 useEffect(() => {
 if (!show) return;
 const fetchBillings = async () => {
 setLoading(true);
 try {
 const params = new URLSearchParams({ limit: 500 });
 if (filters.project) params.append("project", filters.project);
 if (filters.workOrder) params.append("workOrder", filters.workOrder);
 if (filters.status) params.append("status", filters.status);
 const res = await api.get(`/billing?${params}`);
 setBillings(res.data?.data || []);
 } catch {
 setBillings([]);
 } finally {
 setLoading(false);
 }
 };
 fetchBillings();
 }, [show, filters.project, filters.workOrder, filters.status]);

 const totals = useMemo(
    () =>
      billings.reduce(
        (acc, b) => {
          const deduction = Number(b.totalDeductionAmount || 0);
          const settled = Number(b.paidAmount || 0) + deduction;
          return {
            total: acc.total + Number(b.totalAmount || 0),
            paid: acc.paid + Number(b.paidAmount || 0),
            deductions: acc.deductions + deduction,
            pending:
              acc.pending +
              Math.max(Number(b.totalAmount || 0) - settled, 0),
          };
        },
        { total: 0, paid: 0, deductions: 0, pending: 0 }
      ),
    [billings]
  );

 const [exporting, setExporting] = useState(false);

 const handleExport = async () => {
 if (!filters.project && !filters.workOrder) return;
 setExporting(true);
 try {
 const params = new URLSearchParams();
 if (filters.project) params.append("project", filters.project);
 if (filters.workOrder) params.append("workOrder", filters.workOrder);

 const res = await api.get(`/billing/recovery-statement?${params}`, {
 responseType: "blob",
 });

 const projectObj = projects.find((p) => p._id === filters.project);
 const code = projectObj?.projectCode || projectObj?.projectName?.substring(0, 10) || "Project";
 const url = window.URL.createObjectURL(new Blob([res.data]));
 const link = document.createElement("a");
 link.href = url;
 link.setAttribute("download", `Recovery_Statement_${code}.xlsx`);
 document.body.appendChild(link);
 link.click();
 link.remove();
 window.URL.revokeObjectURL(url);
 } catch {
 /* silent */
 } finally {
 setExporting(false);
 }
 };

 return (
 <Modal show={show} onHide={onHide} size="xl" centered>
 <Modal.Header closeButton>
 <Modal.Title>
 <i className="fas fa-file-invoice-dollar text-success me-2" />
 Recovery Statement
 </Modal.Title>
 </Modal.Header>
 <Modal.Body>
 {/* Filters */}
 <Row className="g-2 mb-3">
 {/* Project filter — hidden/locked when parent dashboard has a project selected */}
 {!projectId && (
 <Col md={4}>
 <Form.Label className="small fw-semibold">Project</Form.Label>
 <Form.Select
 size="sm"
 value={filters.project}
 onChange={(e) =>
 setFilters({ project: e.target.value, workOrder: "", status: filters.status })
 }
 >
 <option value="">All Projects</option>
 {projects.map((pr) => (
 <option key={pr._id} value={pr._id}>
 {pr.projectCode ? `${pr.projectCode} - ` : ""}
 {pr.projectName}
 </option>
 ))}
 </Form.Select>
 </Col>
 )}
 <Col md={projectId ? 6 : 4}>
 <Form.Label className="small fw-semibold">Work Order</Form.Label>
 <Form.Select
 size="sm"
 value={filters.workOrder}
 onChange={(e) =>
 setFilters((p) => ({ ...p, workOrder: e.target.value }))
 }
 >
 <option value="">All Work Orders</option>
 {filteredWOs.map((w) => (
 <option key={w._id} value={w._id}>
 {w.woNo || w.woNumber}
 </option>
 ))}
 </Form.Select>
 </Col>
 <Col md={projectId ? 6 : 4}>
 <Form.Label className="small fw-semibold">Status</Form.Label>
 <Form.Select
 size="sm"
 value={filters.status}
 onChange={(e) =>
 setFilters((p) => ({ ...p, status: e.target.value }))
 }
 >
 <option value="">All Statuses</option>
 {["Draft", "Submitted", "Approved", "Partially Paid", "Paid", "Rejected"].map(
 (s) => (
 <option key={s} value={s}>
 {s}
 </option>
 )
 )}
 </Form.Select>
 </Col>
 </Row>

 {/* Summary row */}
 <Row className="g-2 mb-3">
 {[
 { label: "Total Billed", value: fmt(totals.total), color: "#0078D7" },
 { label: "Amount Recovered", value: fmt(totals.paid), color: "#28A745" },
 { label: "Pending Recovery", value: fmt(totals.pending), color: "#DC3545" },
 {
 label: "Recovery %",
 value: totals.total > 0 ? fmtPct((totals.paid / totals.total) * 100) : "0%",
 color: "#F47920",
 },
 ].map((c) => (
 <Col md={3} key={c.label}>
 <div
 className="rounded p-3 text-center"
 style={{ background: `${c.color}12`, border: `1px solid ${c.color}30` }}
 >
 <p className="mb-1 small text-muted">{c.label}</p>
 <h6 className="fw-bold mb-0" style={{ color: c.color }}>
 {c.value}
 </h6>
 </div>
 </Col>
 ))}
 </Row>

 {loading ? (
 <div className="text-center py-4">
 <Spinner animation="border" variant="primary" size="sm" />
 </div>
 ) : (
 <div className="table-responsive" style={{ maxHeight: 400, overflowY: "auto" }}>
 <Table bordered hover size="sm" className="mb-0 align-middle" style={{ fontSize: 13 }}>
 <thead className="table-light sticky-top">
 <tr>
 <th>#</th>
 <th>Invoice No</th>
 <th>Work Order</th>
 <th>Status</th>
 <th className="text-end">Total</th>
 <th className="text-end">Recovered</th>
 <th className="text-end">Pending</th>
 <th>Date</th>
 </tr>
 </thead>
 <tbody>
 {billings.length === 0 ? (
 <tr>
 <td colSpan={8} className="text-center text-muted py-4">
 No records found
 </td>
 </tr>
 ) : (
 billings.map((b, i) => {
 const deduction = Number(b.totalDeductionAmount || 0);
 const pending = Math.max(
 Number(b.totalAmount || 0) - (Number(b.paidAmount || 0) + deduction),
 0
 );
 const woObj =
 typeof b.workOrder === "object"
 ? b.workOrder
 : workOrders.find((w) => w._id === b.workOrder);
 return (
 <tr key={b._id}>
 <td>{i + 1}</td>
 <td>
 <strong className="text-primary">{b.invoiceNumber}</strong>
 {b.advisedInvoiceDoc && (
 <span className="badge bg-light text-primary border border-primary ms-1" style={{ fontSize: "9px" }}>
 Advice
 </span>
 )}
 </td>
 <td>{woObj?.woNo || woObj?.woNumber || "—"}</td>
 <td>
 <Badge bg={billingBadge(b.status)} style={{ fontSize: 10 }}>
 {b.status}
 </Badge>
 </td>
 <td className="text-end">{fmt(b.totalAmount)}</td>
 <td className="text-end text-success fw-semibold">
 {fmt(b.paidAmount)}
 {deduction > 0 && (
 <div className="badge bg-light text-danger border border-danger d-block mt-1" style={{ fontSize: "10px" }}>
 Ded: -{fmt(deduction)}
 </div>
 )}
 </td>
 <td
 className={`text-end fw-semibold ${
 pending > 0 ? "text-danger" : "text-muted"
 }`}
 >
 {fmt(pending)}
 </td>
 <td>
 {fmtDate(b.createdAt)}
 </td>
 </tr>
 );
 })
 )}
 </tbody>
 {billings.length > 0 && (
 <tfoot className="table-light fw-bold">
 <tr>
 <td colSpan={4}>Total ({billings.length} invoices)</td>
 <td className="text-end">{fmt(totals.total)}</td>
 <td className="text-end text-success">
 {fmt(totals.paid)}
 {totals.deductions > 0 && (
 <div className="badge bg-light text-danger border border-danger d-block mt-1" style={{ fontSize: "10px" }}>
 Ded: -{fmt(totals.deductions)}
 </div>
 )}
 </td>
 <td className="text-end text-danger">{fmt(totals.pending)}</td>
 <td />
 </tr>
 </tfoot>
 )}
 </Table>
 </div>
 )}
 </Modal.Body>
 <Modal.Footer>
 <Button
 variant="success"
 size="sm"
 onClick={handleExport}
 disabled={exporting || (!filters.project && !filters.workOrder)}
 >
 {exporting ? (
 <><Spinner animation="border" size="sm" className="me-1" /> Generating...</>
 ) : (
 <><i className="fas fa-file-excel me-1" /> Export Excel</>
 )}
 </Button>
 <Button variant="secondary" size="sm" onClick={onHide}>
 Close
 </Button>
 </Modal.Footer>
 </Modal>
 );
};

// Advance Statement Modal 

const AdvanceStatementModal = ({ show, onHide, projectId, projects, workOrders }) => {
 const [selProject, setSelProject] = useState(projectId || "");
 const [selWO, setSelWO] = useState("");
 const [data, setData] = useState(null);
 const [loading, setLoading] = useState(false);

 // Sync project when parent selection changes or modal opens
 useEffect(() => {
 if (!show) return;
 setSelProject(projectId || "");
 setSelWO("");
 setData(null);
 }, [show, projectId]);

 // WOs filtered by selected project
 const filteredWOs = useMemo(
 () =>
 workOrders.filter((w) =>
 !selProject ||
 String(w.project?._id || w.project || "") === String(selProject)
 ),
 [workOrders, selProject]
 );

 // Fetch payment data for the selected project whenever project changes
 useEffect(() => {
 if (!show || !selProject) {
 setData(null);
 setSelWO("");
 return;
 }
 const fetchData = async () => {
 setLoading(true);
 try {
 const res = await api.get(`/payments/project/${selProject}`);
 setData(res.data || null);
 } catch {
 setData(null);
 } finally {
 setLoading(false);
 }
 };
 fetchData();
 }, [show, selProject]);

 // Find the selected work order inside fetched data
 const workOrder = useMemo(() => {
 if (!data || !selWO) return null;
 return (data.data || []).find((w) => w._id === selWO) || null;
 }, [data, selWO]);

 const deductions = workOrder?.deductions || [];
 const paymentTerms = workOrder?.paymentTerms || [];

 // Project-level summary when no WO is selected
 const projectSummary = useMemo(() => {
 if (!data || selWO) return null;
 const wos = data.data || [];
 return wos.reduce(
 (acc, w) => ({
 totalValue: acc.totalValue + Number(w.value || 0),
 totalDeductionAmount: acc.totalDeductionAmount + Number(w.totalDeductionAmount || 0),
 totalPayableAmount: acc.totalPayableAmount + Number(w.payableAmount || 0),
 }),
 { totalValue: 0, totalDeductionAmount: 0, totalPayableAmount: 0 }
 );
 }, [data, selWO]);

 const [exporting, setExporting] = useState(false);

 const handleExport = async () => {
 if (!selProject) return;
 setExporting(true);
 try {
 const params = new URLSearchParams();
 params.append("project", selProject);
 if (selWO) params.append("workOrder", selWO);

 const res = await api.get(`/billing/advance-statement?${params}`, {
 responseType: "blob",
 });

 const projectObj = projects.find((p) => p._id === selProject);
 const code = projectObj?.projectCode || projectObj?.projectName?.substring(0, 10) || "Project";
 const url = window.URL.createObjectURL(new Blob([res.data]));
 const link = document.createElement("a");
 link.href = url;
 link.setAttribute("download", `Advance_Statement_${code}.xlsx`);
 document.body.appendChild(link);
 link.click();
 link.remove();
 window.URL.revokeObjectURL(url);
 } catch {
 /* silent */
 } finally {
 setExporting(false);
 }
 };

 return (
 <Modal show={show} onHide={onHide} size="xl" centered>
 <Modal.Header closeButton>
 <Modal.Title>
 <i className="fas fa-hand-holding-usd text-warning me-2" />
 Advance &amp; Deduction Statement
 </Modal.Title>
 </Modal.Header>
 <Modal.Body>
 {/* Filters */}
 <Row className="g-2 mb-4">
 {/* Project dropdown — locked when parent has a project selected */}
 {!projectId && (
 <Col md={6}>
 <Form.Label className="small fw-semibold">Project</Form.Label>
 <Form.Select
 size="sm"
 value={selProject}
 onChange={(e) => { setSelProject(e.target.value); setSelWO(""); }}
 >
 <option value="">-- Select Project --</option>
 {projects.map((p) => (
 <option key={p._id} value={p._id}>
 {p.projectCode ? `${p.projectCode} - ` : ""}{p.projectName}
 </option>
 ))}
 </Form.Select>
 </Col>
 )}
 <Col md={projectId ? 12 : 6}>
 <Form.Label className="small fw-semibold">
 Work Order <span className="text-muted fw-normal">(optional — leave blank for all)</span>
 </Form.Label>
 <Form.Select
 size="sm"
 value={selWO}
 onChange={(e) => setSelWO(e.target.value)}
 disabled={!selProject}
 >
 <option value="">All Work Orders</option>
 {filteredWOs.map((w) => (
 <option key={w._id} value={w._id}>
 {w.woNo || w.woNumber}
 </option>
 ))}
 </Form.Select>
 </Col>
 </Row>

 {loading && (
 <div className="text-center py-4">
 <Spinner animation="border" variant="primary" size="sm" />
 </div>
 )}

 {/* No project selected */}
 {!loading && !selProject && (
 <div className="text-center py-4 text-muted">
 <i className="fas fa-hand-point-up fs-2 mb-2 d-block opacity-25" />
 Select a project to view the advance statement
 </div>
 )}

 {/* Project selected, no WO — show all WOs summary */}
 {!loading && selProject && data && !selWO && (
 <>
 <Row className="g-2 mb-4">
 {[
 { label: "Total WO Value", value: fmt(projectSummary?.totalValue), color: "#0078D7" },
 { label: "Total Deductions", value: fmt(projectSummary?.totalDeductionAmount), color: "#DC3545" },
 { label: "Net Receivable", value: fmt(projectSummary?.totalPayableAmount), color: "#28A745" },
 {
 label: "Work Orders",
 value: (data.data || []).length,
 color: "#6F42C1",
 },
 ].map((c) => (
 <Col md={3} key={c.label}>
 <div
 className="rounded p-3 text-center"
 style={{ background: `${c.color}12`, border: `1px solid ${c.color}30` }}
 >
 <p className="mb-1 small text-muted">{c.label}</p>
 <h6 className="fw-bold mb-0" style={{ color: c.color }}>{c.value}</h6>
 </div>
 </Col>
 ))}
 </Row>

 <h6 className="fw-bold mb-2">
 <i className="fas fa-list text-primary me-1" />
 All Work Orders — Deduction Summary
 </h6>
 <div className="table-responsive" style={{ maxHeight: 420, overflowY: "auto" }}>
 <Table bordered hover size="sm" className="mb-0 align-middle" style={{ fontSize: 13 }}>
 <thead className="table-light sticky-top">
 <tr>
 <th>#</th>
 <th>Work Order</th>
 <th className="text-end">WO Value</th>
 <th className="text-end">Total Deduction</th>
 <th className="text-end">Deduction %</th>
 <th className="text-end">Net Receivable</th>
 <th>Action</th>
 </tr>
 </thead>
 <tbody>
 {(data.data || []).length === 0 ? (
 <tr>
 <td colSpan={7} className="text-center text-muted py-4">No work orders found</td>
 </tr>
 ) : (
 (data.data || []).map((wo, i) => (
 <tr key={wo._id}>
 <td>{i + 1}</td>
 <td><strong className="text-primary">{wo.woNo || wo.woNumber}</strong></td>
 <td className="text-end">{fmt(wo.value)}</td>
 <td className="text-end text-danger fw-semibold">{fmt(wo.totalDeductionAmount)}</td>
 <td className="text-end">{fmtPct(wo.totalDeductionPercentage)}</td>
 <td className="text-end text-success fw-semibold">{fmt(wo.payableAmount)}</td>
 <td>
 <Button
 size="sm"
 variant="outline-primary"
 style={{ fontSize: 11, padding: "2px 8px" }}
 onClick={() => setSelWO(wo._id)}
 >
 Details
 </Button>
 </td>
 </tr>
 ))
 )}
 </tbody>
 {(data.data || []).length > 0 && (
 <tfoot className="table-light fw-bold">
 <tr>
 <td colSpan={2}>Total ({(data.data || []).length} WOs)</td>
 <td className="text-end">{fmt(projectSummary?.totalValue)}</td>
 <td className="text-end text-danger">{fmt(projectSummary?.totalDeductionAmount)}</td>
 <td />
 <td className="text-end text-success">{fmt(projectSummary?.totalPayableAmount)}</td>
 <td />
 </tr>
 </tfoot>
 )}
 </Table>
 </div>
 </>
 )}

 {/* WO selected — show detailed deductions + payment terms */}
 {!loading && selWO && workOrder && (
 <>
 <div className="d-flex align-items-center gap-2 mb-3">
 <Button
 size="sm"
 variant="outline-secondary"
 onClick={() => setSelWO("")}
 >
 <i className="fas fa-arrow-left me-1" /> All Work Orders
 </Button>
 <span className="text-muted small">
 Viewing: <strong>{workOrder.woNo || workOrder.woNumber}</strong>
 </span>
 </div>

 <Row className="g-2 mb-4">
 {[
 { label: "WO Value", value: fmt(workOrder.value), color: "#0078D7" },
 { label: "Total Deduction", value: fmt(workOrder.totalDeductionAmount), color: "#DC3545" },
 { label: "Deduction %", value: fmtPct(workOrder.totalDeductionPercentage), color: "#F47920" },
 { label: "Net Receivable", value: fmt(workOrder.payableAmount), color: "#28A745" },
 ].map((c) => (
 <Col md={3} key={c.label}>
 <div
 className="rounded p-3 text-center"
 style={{ background: `${c.color}12`, border: `1px solid ${c.color}30` }}
 >
 <p className="mb-1 small text-muted">{c.label}</p>
 <h6 className="fw-bold mb-0" style={{ color: c.color }}>{c.value}</h6>
 </div>
 </Col>
 ))}
 </Row>

 <h6 className="fw-bold mb-2">
 <i className="fas fa-minus-circle text-danger me-1" />
 Deductions / Recovery
 </h6>
 <div className="table-responsive mb-4">
 <Table bordered hover size="sm" className="mb-0 align-middle" style={{ fontSize: 13 }}>
 <thead className="table-light">
 <tr>
 <th>#</th>
 <th>Deduction Type</th>
 <th className="text-end">%</th>
 <th className="text-end">Amount</th>
 <th>Remarks</th>
 </tr>
 </thead>
 <tbody>
 {deductions.length === 0 ? (
 <tr>
 <td colSpan={5} className="text-center text-muted">No deductions configured</td>
 </tr>
 ) : (
 deductions.map((d, i) => (
 <tr key={d._id || i}>
 <td>{i + 1}</td>
 <td>{d.name?.name || d.name || "—"}</td>
 <td className="text-end">{fmtPct(d.percentage)}</td>
 <td className="text-end text-danger fw-semibold">{fmt(d.amount)}</td>
 <td className="text-muted">{d.remarks || "—"}</td>
 </tr>
 ))
 )}
 </tbody>
 </Table>
 </div>

 <h6 className="fw-bold mb-2">
 <i className="fas fa-list-ol text-primary me-1" />
 Payment Terms (Advance Tracking)
 </h6>
 <div className="table-responsive">
 <Table bordered hover size="sm" className="mb-0 align-middle" style={{ fontSize: 13 }}>
 <thead className="table-light">
 <tr>
 <th>#</th>
 <th>Stage / Milestone</th>
 <th className="text-end">%</th>
 <th className="text-end">Without GST</th>
 <th className="text-end">GST</th>
 <th className="text-end">With GST</th>
 <th className="text-end">Deduction</th>
 <th className="text-end">Net Receivable</th>
 </tr>
 </thead>
 <tbody>
 {paymentTerms.length === 0 ? (
 <tr>
 <td colSpan={8} className="text-center text-muted">No payment terms configured</td>
 </tr>
 ) : (
 paymentTerms.map((t, i) => (
 <tr key={t._id || i}>
 <td>{i + 1}</td>
 <td>{t.stage || t.name || `Stage ${i + 1}`}</td>
 <td className="text-end">{fmtPct(t.percentage)}</td>
 <td className="text-end">{fmt(t.amountWithoutGst)}</td>
 <td className="text-end">{fmt(t.gstAmount)}</td>
 <td className="text-end">{fmt(t.amountWithGst)}</td>
 <td className="text-end text-danger">{fmt(t.deductionAmount)}</td>
 <td className="text-end text-success fw-semibold">{fmt(t.payableAmountWithGst)}</td>
 </tr>
 ))
 )}
 </tbody>
 </Table>
 </div>
 </>
 )}

 {!loading && selWO && !workOrder && (
 <div className="text-center py-4 text-muted">
 <i className="fas fa-inbox fs-2 mb-2 d-block opacity-25" />
 No data available for this work order
 </div>
 )}
 </Modal.Body>
 <Modal.Footer>
 <Button
 variant="success"
 size="sm"
 onClick={handleExport}
 disabled={exporting || !selProject}
 >
 {exporting ? (
 <><Spinner animation="border" size="sm" className="me-1" /> Generating...</>
 ) : (
 <><i className="fas fa-file-excel me-1" /> Export Excel</>
 )}
 </Button>
 <Button variant="secondary" size="sm" onClick={onHide}>
 Close
 </Button>
 </Modal.Footer>
 </Modal>
 );
};

// Cost vs Budget Modal 

const CostVsBudgetModal = ({ show, onHide, projectId, projects, workOrders }) => {
 const [selProject, setSelProject] = useState(projectId || "");
 const [selWO, setSelWO] = useState("");
 const [woData, setWoData] = useState(null);
 const [projectBilling, setProjectBilling] = useState(null); // billing for whole project
 const [costData, setCostData] = useState(null);
 const [billingData, setBillingData] = useState({ total: 0, paid: 0 });
 const [loading, setLoading] = useState(false);

 // Sync project on open / parent change
 useEffect(() => {
 if (!show) return;
 setSelProject(projectId || "");
 setSelWO("");
 setWoData(null);
 setProjectBilling(null);
 setCostData(null);
 setBillingData({ total: 0, paid: 0 });
 }, [show, projectId]);

 // WOs filtered by selected project
 const filteredWOs = useMemo(
 () =>
 workOrders.filter((w) =>
 !selProject ||
 String(w.project?._id || w.project || "") === String(selProject)
 ),
 [workOrders, selProject]
 );

 // Fetch project-level billing summary when project is selected but no WO
 useEffect(() => {
 if (!show || !selProject || selWO) {
 setProjectBilling(null);
 return;
 }
 const fetchProjectData = async () => {
 setLoading(true);
 try {
 const [billingRes, costRes] = await Promise.all([
 api.get(`/billing/overview?project=${selProject}`),
 api.get(`/billing/cost-utilization?project=${selProject}`),
 ]);
 setProjectBilling(billingRes.data?.data || null);
 setCostData(costRes.data?.data || null);
 } catch {
 setProjectBilling(null);
 setCostData(null);
 } finally {
 setLoading(false);
 }
 };
 fetchProjectData();
 }, [show, selProject, selWO]);

 // Fetch WO-specific data
 useEffect(() => {
 if (!show || !selWO) {
 setWoData(null);
 setBillingData({ total: 0, paid: 0 });
 return;
 }
 const fetchWOData = async () => {
 setLoading(true);
 try {
 const [woRes, billRes, costRes] = await Promise.all([
 api.get(`/work-orders/${selWO}`),
 api.get(`/billing?workOrder=${selWO}&limit=500`),
 api.get(`/billing/cost-utilization?workOrder=${selWO}`),
 ]);
 const wo = woRes.data?.data || woRes.data || null;
 setWoData(wo);
 setCostData(costRes.data?.data || null);
 const bills = billRes.data?.data || [];
 setBillingData({
 total: bills.reduce((s, b) => s + Number(b.totalAmount || 0), 0),
 paid: bills.reduce((s, b) => s + Number(b.paidAmount || 0), 0),
 });
 } catch {
 setWoData(null);
 setCostData(null);
 setBillingData({ total: 0, paid: 0 });
 } finally {
 setLoading(false);
 }
 };
 fetchWOData();
 }, [show, selWO]);

 // WO-level derived metrics 
 const woValue = Number(woData?.value || 0);
 const woMaterial = Number(woData?.materialBudget || 0);
 const woLabour = Number(woData?.labourBudget || 0);
 const woContract = Number(woData?.contractBudget || 0);
 const woOther = Number(woData?.otherBudget || 0);
 const budget = woMaterial + woLabour + woContract + woOther;
 const budgetWithGst = Number(woData?.totalWithGst || woData?.totalAmount || woData?.value || 0);
 const costSummary = costData?.summary || {};
 const actualCost = Number(costSummary.actualCost || 0);
 const poCost = Number(costSummary.purchaseOrderCost || 0);
 const expenseCost = Number(costSummary.expenseCost || 0);
 const contractorCost = Number(costSummary.contractorBillingCost || 0);
 const billed = billingData.total;
 const received = billingData.paid;
 const remaining = Math.max(budget - actualCost, 0);
 const utilPct = budget > 0 ? Math.min(100, Math.round((actualCost / budget) * 100)) : 0;
 const collPct = billed > 0 ? Math.min(100, Math.round((received / billed) * 100)) : 0;

 const woChartData = [
 { name: "Total Budget", value: budget, fill: "#6F42C1" },
 { name: "WO Value", value: woValue, fill: "#0078D7" },
 { name: "Actual Cost", value: actualCost, fill: "#F47920" },
 { name: "Received", value: received, fill: "#28A745" },
 { name: "Remaining", value: remaining, fill: "#DC3545" },
 ];

 // Project-level derived metrics 
 const projSummary = projectBilling?.summary || {};
 const projBilledTotal = Number(projSummary.totalAmount || 0);
 const projPaid = Number(projSummary.paidAmount || 0);
 const projPending = Number(projSummary.pendingAmount || 0);
 const projCollPct = projBilledTotal > 0
 ? Math.min(100, Math.round((projPaid / projBilledTotal) * 100)) : 0;

 // Aggregate ALL budget fields across filtered WOs
 const projBudgetComponents = useMemo(() => {
 return filteredWOs.reduce(
 (acc, w) => ({
 woValue: acc.woValue + Number(w.value || 0),
 materialBudget: acc.materialBudget + Number(w.materialBudget || 0),
 labourBudget: acc.labourBudget + Number(w.labourBudget || 0),
 contractBudget: acc.contractBudget + Number(w.contractBudget || 0),
 otherBudget: acc.otherBudget + Number(w.otherBudget || 0),
 }),
 { woValue: 0, materialBudget: 0, labourBudget: 0, contractBudget: 0, otherBudget: 0 }
 );
 }, [filteredWOs]);

 const projTotalBudget =
 projBudgetComponents.materialBudget +
 projBudgetComponents.labourBudget +
 projBudgetComponents.contractBudget +
 projBudgetComponents.otherBudget;

 const projWOValue = projBudgetComponents.woValue;
 const projCostSummary = costData?.summary || {};
 const projActualCost = Number(projCostSummary.actualCost || 0);
 const projPoCost = Number(projCostSummary.purchaseOrderCost || 0);
 const projExpenseCost = Number(projCostSummary.expenseCost || 0);
 const projContractorCost = Number(projCostSummary.contractorBillingCost || 0);
 const projRemaining = Math.max(projTotalBudget - projActualCost, 0);
 const projUtilPct = projTotalBudget > 0
 ? Math.min(100, Math.round((projActualCost / projTotalBudget) * 100)) : 0;

 const projChartData = [
 { name: "Total Budget", value: projTotalBudget, fill: "#6F42C1" },
 { name: "WO Value", value: projWOValue, fill: "#0078D7" },
 { name: "Actual Cost", value: projActualCost, fill: "#F47920" },
 { name: "Received", value: projPaid, fill: "#28A745" },
 { name: "Remaining", value: projRemaining, fill: "#DC3545" },
 ];

 const costRowsByWo = useMemo(() => {
 const rows = costData?.workOrders || [];
 return rows.reduce((acc, row) => {
 acc[row._id] = row;
 return acc;
 }, {});
 }, [costData]);

 const [exporting, setExporting] = useState(false);

 const handleExport = async () => {
 if (!selProject) return;
 setExporting(true);
 try {
 const params = new URLSearchParams();
 params.append("project", selProject);
 if (selWO) params.append("workOrder", selWO);

 const res = await api.get(`/billing/cost-vs-budget?${params}`, {
 responseType: "blob",
 });

 const projectObj = projects.find((p) => p._id === selProject);
 const code = projectObj?.projectCode || projectObj?.projectName?.substring(0, 10) || "Project";
 const url = window.URL.createObjectURL(new Blob([res.data]));
 const link = document.createElement("a");
 link.href = url;
 link.setAttribute("download", `Cost_vs_Budget_${code}.xlsx`);
 document.body.appendChild(link);
 link.click();
 link.remove();
 window.URL.revokeObjectURL(url);
 } catch {
 /* silent */
 } finally {
 setExporting(false);
 }
 };

 return (
 <Modal show={show} onHide={onHide} size="xl" centered>
 <Modal.Header closeButton>
 <Modal.Title>
 <i className="fas fa-balance-scale text-primary me-2" />
 Cost vs Budget
 </Modal.Title>
 </Modal.Header>
 <Modal.Body>
 {/* Filters */}
 <Row className="g-2 mb-4">
 {!projectId && (
 <Col md={6}>
 <Form.Label className="small fw-semibold">Project</Form.Label>
 <Form.Select
 size="sm"
 value={selProject}
 onChange={(e) => { setSelProject(e.target.value); setSelWO(""); }}
 >
 <option value="">-- Select Project --</option>
 {projects.map((p) => (
 <option key={p._id} value={p._id}>
 {p.projectCode ? `${p.projectCode} - ` : ""}{p.projectName}
 </option>
 ))}
 </Form.Select>
 </Col>
 )}
 <Col md={projectId ? 12 : 6}>
 <Form.Label className="small fw-semibold">
 Work Order <span className="text-muted fw-normal">(optional — leave blank for project view)</span>
 </Form.Label>
 <Form.Select
 size="sm"
 value={selWO}
 onChange={(e) => setSelWO(e.target.value)}
 disabled={!selProject}
 >
 <option value="">All Work Orders</option>
 {filteredWOs.map((w) => (
 <option key={w._id} value={w._id}>
 {w.woNo || w.woNumber}
 </option>
 ))}
 </Form.Select>
 </Col>
 </Row>

 {loading && (
 <div className="text-center py-4">
 <Spinner animation="border" variant="primary" size="sm" />
 </div>
 )}

 {/* No project */}
 {!loading && !selProject && (
 <div className="text-center py-4 text-muted">
 <i className="fas fa-hand-point-up fs-2 mb-2 d-block opacity-25" />
 Select a project to view the cost vs budget analysis
 </div>
 )}

 {/* Project view (no WO) */}
 {!loading && selProject && !selWO && (
 <>
 {/* Budget Summary breakdown */}
 <Card className="border-0 shadow-sm mb-4">
 <Card.Header className="bg-white border-0 fw-bold pt-3 pb-0" style={{ fontSize: 13 }}>
 <i className="fas fa-calculator text-primary me-2" />
 Budget Summary
 </Card.Header>
 <Card.Body className="pt-2 pb-3">
 <Row className="g-2 text-center">
 {[
 { label: "WO Contract Value", val: projBudgetComponents.woValue, color: "#0078D7" },
 { label: "Material Budget", val: projBudgetComponents.materialBudget, color: "#17A2B8" },
 { label: "Labour Budget", val: projBudgetComponents.labourBudget, color: "#6F42C1" },
 { label: "Contract Budget", val: projBudgetComponents.contractBudget, color: "#F47920" },
 { label: "Other Expenses", val: projBudgetComponents.otherBudget, color: "#E83E8C" },
 ].map((item) => (
 <Col key={item.label}>
 <small className="text-muted d-block" style={{ fontSize: 10 }}>{item.label}</small>
 <span className="fw-semibold" style={{ fontSize: 13, color: item.color }}>
 {fmtCr(item.val)}
 </span>
 </Col>
 ))}
 <Col>
 <div className="rounded px-2 py-1" style={{ background: "#0078D712", border: "1px solid #0078D730" }}>
 <small className="fw-bold text-primary d-block" style={{ fontSize: 10 }}>Total Budget</small>
 <span className="fw-bold text-primary" style={{ fontSize: 14 }}>
 {fmtCr(projTotalBudget)}
 </span>
 </div>
 </Col>
 </Row>
 </Card.Body>
 </Card>

 <Row className="g-2 mb-4">
 {[
 { label: "Total Budget", value: fmtCr(projTotalBudget), color: "#6F42C1", icon: "fa-calculator" },
 { label: "Actual Cost", value: fmtCr(projActualCost), color: "#F47920", icon: "fa-wallet" },
 { label: "PO Cost", value: fmtCr(projPoCost), color: "#17A2B8", icon: "fa-file-contract" },
 { label: "Expenses", value: fmtCr(projExpenseCost), color: "#E83E8C", icon: "fa-receipt" },
 ].map((c) => (
 <Col md={3} key={c.label}>
 <div
 className="rounded p-3 text-center"
 style={{ background: `${c.color}12`, border: `1px solid ${c.color}30` }}
 >
 <i className={`fas ${c.icon} mb-2`} style={{ color: c.color, fontSize: 18 }} />
 <p className="mb-1 small text-muted">{c.label}</p>
 <h5 className="fw-bold mb-0" style={{ color: c.color }}>{c.value}</h5>
 </div>
 </Col>
 ))}
 </Row>

 <Card className="border-0 shadow-sm mb-4">
 <Card.Body className="py-3">
 <Row className="g-2 text-center">
 {[
 { label: "PO / Material Commitments", val: projPoCost, color: "#17A2B8" },
 { label: "Direct Expenses", val: projExpenseCost, color: "#E83E8C" },
 { label: "Contractor Bills", val: projContractorCost, color: "#F47920" },
 { label: "Budget Balance", val: projRemaining, color: "#28A745" },
 ].map((item) => (
 <Col md={3} key={item.label}>
 <small className="text-muted d-block" style={{ fontSize: 10 }}>{item.label}</small>
 <span className="fw-bold" style={{ fontSize: 14, color: item.color }}>
 {fmtCr(item.val)}
 </span>
 </Col>
 ))}
 </Row>
 </Card.Body>
 </Card>

 <Card className="border-0 shadow-sm mb-4">
 <Card.Body>
 <Row className="g-3">
 <Col md={6}>
 <div className="d-flex justify-content-between small mb-1">
 <span className="fw-semibold">Budget Utilisation</span>
 <span className="fw-bold">{projUtilPct}%</span>
 </div>
 <ProgressBar
 now={projUtilPct}
 variant={projUtilPct > 90 ? "danger" : projUtilPct > 70 ? "warning" : "primary"}
 style={{ height: 12, borderRadius: 8 }}
 />
 <small className="text-muted mt-1 d-block">
 {fmtCr(projActualCost)} actual cost out of {fmtCr(projTotalBudget)} total budget
 </small>
 </Col>
 <Col md={6}>
 <div className="d-flex justify-content-between small mb-1">
 <span className="fw-semibold">Collection Rate</span>
 <span className="fw-bold">{projCollPct}%</span>
 </div>
 <ProgressBar
 now={projCollPct}
 variant={projCollPct >= 90 ? "success" : projCollPct >= 60 ? "warning" : "danger"}
 style={{ height: 12, borderRadius: 8 }}
 />
 <small className="text-muted mt-1 d-block">
 {fmtCr(projPaid)} received out of {fmtCr(projBilledTotal)} billed
 </small>
 </Col>
 </Row>
 </Card.Body>
 </Card>

 <Card className="border-0 shadow-sm mb-4">
 <Card.Header className="bg-white border-0 fw-bold">Budget Comparison Chart</Card.Header>
 <Card.Body>
 <ResponsiveContainer width="100%" height={260}>
 <BarChart data={projChartData} margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
 <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
 <XAxis dataKey="name" tick={{ fontSize: 11 }} />
 <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => fmtCr(v)} />
 <Tooltip formatter={(v) => [fmt(v), "Amount"]} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
 <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={60}>
 {projChartData.map((entry, i) => (
 <Cell key={i} fill={entry.fill} />
 ))}
 </Bar>
 </BarChart>
 </ResponsiveContainer>
 </Card.Body>
 </Card>

 {/* Work orders drill-down table */}
 <h6 className="fw-bold mb-2">
 <i className="fas fa-list text-primary me-1" />
 Work Orders Breakdown
 </h6>
 <div className="table-responsive" style={{ maxHeight: 300, overflowY: "auto" }}>
 <Table bordered hover size="sm" className="mb-0 align-middle" style={{ fontSize: 13 }}>
 <thead className="table-light sticky-top">
 <tr>
 <th>#</th>
 <th>Work Order</th>
 <th>Scope</th>
 <th className="text-end">WO Value</th>
 <th className="text-end">Total Budget</th>
 <th className="text-end">Actual Cost</th>
 <th>Utilisation</th>
 <th>Action</th>
 </tr>
 </thead>
 <tbody>
 {filteredWOs.length === 0 ? (
 <tr>
 <td colSpan={8} className="text-center text-muted py-3">No work orders found</td>
 </tr>
 ) : (
 filteredWOs.map((w, i) => {
 const wTotal =
 Number(w.materialBudget || 0) +
 Number(w.labourBudget || 0) +
 Number(w.contractBudget || 0) +
 Number(w.otherBudget || 0);
 const wCost = costRowsByWo[w._id] || {};
 const wActual = Number(wCost.actualCost || 0);
 const wUtil = wTotal > 0 ? Math.min(100, Math.round((wActual / wTotal) * 100)) : 0;
 return (
 <tr key={w._id}>
 <td>{i + 1}</td>
 <td><strong className="text-primary">{w.woNo || w.woNumber}</strong></td>
 <td className="text-muted" style={{ maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
 {w.scope || "—"}
 </td>
 <td className="text-end">{fmtCr(w.value)}</td>
 <td className="text-end fw-semibold text-primary">{fmtCr(wTotal)}</td>
 <td className="text-end fw-semibold text-warning">{fmtCr(wActual)}</td>
 <td>
 <div className="d-flex align-items-center gap-2">
 <ProgressBar
 now={wUtil}
 variant={wUtil > 90 ? "danger" : wUtil > 70 ? "warning" : "primary"}
 style={{ flex: 1, height: 6, borderRadius: 4 }}
 />
 <span style={{ width: 36, fontSize: 11 }}>{wUtil}%</span>
 </div>
 </td>
 <td>
 <Button
 size="sm"
 variant="outline-primary"
 style={{ fontSize: 11, padding: "2px 8px" }}
 onClick={() => setSelWO(w._id)}
 >
 Analyse
 </Button>
 </td>
 </tr>
 );
 })
 )}
 </tbody>
 </Table>
 </div>
 </>
 )}

 {/* WO view */}
 {!loading && selWO && woData && (
 <>
 <div className="d-flex align-items-center gap-2 mb-3">
 <Button size="sm" variant="outline-secondary" onClick={() => setSelWO("")}>
 <i className="fas fa-arrow-left me-1" /> All Work Orders
 </Button>
 <span className="text-muted small">
 Viewing: <strong>{woData.woNo || woData.woNumber}</strong>
 </span>
 </div>

 {/* WO Budget Summary breakdown */}
 <Card className="border-0 shadow-sm mb-4">
 <Card.Header className="bg-white border-0 fw-bold pt-3 pb-0" style={{ fontSize: 13 }}>
 <i className="fas fa-calculator text-primary me-2" />
 Budget Breakdown
 </Card.Header>
 <Card.Body className="pt-2 pb-3">
 <Row className="g-2 text-center">
 {[
 { label: "WO Contract Value", val: woValue, color: "#0078D7" },
 { label: "Material Budget", val: woMaterial, color: "#17A2B8" },
 { label: "Labour Budget", val: woLabour, color: "#6F42C1" },
 { label: "Contract Budget", val: woContract, color: "#F47920" },
 { label: "Other Expenses", val: woOther, color: "#E83E8C" },
 ].map((item) => (
 <Col key={item.label}>
 <small className="text-muted d-block" style={{ fontSize: 10 }}>{item.label}</small>
 <span className="fw-semibold" style={{ fontSize: 13, color: item.color }}>
 {fmtCr(item.val)}
 </span>
 </Col>
 ))}
 <Col>
 <div className="rounded px-2 py-1" style={{ background: "#0078D712", border: "1px solid #0078D730" }}>
 <small className="fw-bold text-primary d-block" style={{ fontSize: 10 }}>Total Budget</small>
 <span className="fw-bold text-primary" style={{ fontSize: 14 }}>
 {fmtCr(budget)}
 </span>
 </div>
 </Col>
 </Row>
 </Card.Body>
 </Card>

 <Row className="g-2 mb-4">
 {[
 { label: "Total Budget", value: fmtCr(budget), color: "#6F42C1", icon: "fa-calculator" },
 { label: "Actual Cost", value: fmtCr(actualCost), color: "#F47920", icon: "fa-wallet" },
 { label: "PO Cost", value: fmtCr(poCost), color: "#17A2B8", icon: "fa-file-contract" },
 { label: "Expenses", value: fmtCr(expenseCost), color: "#E83E8C", icon: "fa-receipt" },
 ].map((c) => (
 <Col md={3} key={c.label}>
 <div
 className="rounded p-3 text-center"
 style={{ background: `${c.color}12`, border: `1px solid ${c.color}30` }}
 >
 <i className={`fas ${c.icon} mb-2`} style={{ color: c.color, fontSize: 18 }} />
 <p className="mb-1 small text-muted">{c.label}</p>
 <h5 className="fw-bold mb-0" style={{ color: c.color }}>{c.value}</h5>
 </div>
 </Col>
 ))}
 </Row>

 <Card className="border-0 shadow-sm mb-4">
 <Card.Body className="py-3">
 <Row className="g-2 text-center">
 {[
 { label: "PO / Material Commitments", val: poCost, color: "#17A2B8" },
 { label: "Direct Expenses", val: expenseCost, color: "#E83E8C" },
 { label: "Contractor Bills", val: contractorCost, color: "#F47920" },
 { label: "Budget Balance", val: remaining, color: "#28A745" },
 ].map((item) => (
 <Col md={3} key={item.label}>
 <small className="text-muted d-block" style={{ fontSize: 10 }}>{item.label}</small>
 <span className="fw-bold" style={{ fontSize: 14, color: item.color }}>
 {fmtCr(item.val)}
 </span>
 </Col>
 ))}
 </Row>
 </Card.Body>
 </Card>

 <Card className="border-0 shadow-sm mb-4">
 <Card.Body>
 <Row className="g-3">
 <Col md={6}>
 <div className="d-flex justify-content-between small mb-1">
 <span className="fw-semibold">Budget Utilisation</span>
 <span className="fw-bold">{utilPct}%</span>
 </div>
 <ProgressBar
 now={utilPct}
 variant={utilPct > 90 ? "danger" : utilPct > 70 ? "warning" : "primary"}
 style={{ height: 12, borderRadius: 8 }}
 />
 <small className="text-muted mt-1 d-block">
 {fmtCr(actualCost)} actual cost out of {fmtCr(budget)} budget
 </small>
 </Col>
 <Col md={6}>
 <div className="d-flex justify-content-between small mb-1">
 <span className="fw-semibold">Collection Rate</span>
 <span className="fw-bold">{collPct}%</span>
 </div>
 <ProgressBar
 now={collPct}
 variant={collPct >= 90 ? "success" : collPct >= 60 ? "warning" : "danger"}
 style={{ height: 12, borderRadius: 8 }}
 />
 <small className="text-muted mt-1 d-block">
 {fmtCr(received)} received out of {fmtCr(billed)} billed
 </small>
 </Col>
 </Row>
 </Card.Body>
 </Card>

 <Card className="border-0 shadow-sm">
 <Card.Header className="bg-white border-0 fw-bold">Budget Comparison Chart</Card.Header>
 <Card.Body>
 <ResponsiveContainer width="100%" height={280}>
 <BarChart data={woChartData} margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
 <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
 <XAxis dataKey="name" tick={{ fontSize: 11 }} />
 <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => fmtCr(v)} />
 <Tooltip formatter={(v) => [fmt(v), "Amount"]} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
 <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={60}>
 {woChartData.map((entry, i) => (
 <Cell key={i} fill={entry.fill} />
 ))}
 </Bar>
 </BarChart>
 </ResponsiveContainer>
 </Card.Body>
 </Card>
 </>
 )}

 {!loading && selWO && !woData && (
 <div className="text-center py-4 text-muted">
 <i className="fas fa-inbox fs-2 mb-2 d-block opacity-25" />
 No data available for this work order
 </div>
 )}
 </Modal.Body>
 <Modal.Footer>
 <Button
 variant="success"
 size="sm"
 onClick={handleExport}
 disabled={exporting || !selProject}
 >
 {exporting ? (
 <><Spinner animation="border" size="sm" className="me-1" /> Generating...</>
 ) : (
 <><i className="fas fa-file-excel me-1" /> Export Excel</>
 )}
 </Button>
 <Button variant="secondary" size="sm" onClick={onHide}>
 Close
 </Button>
 </Modal.Footer>
 </Modal>
 );
};

// Main Commercial Dashboard 

const CommercialDashboard = () => {
 const [projects, setProjects] = useState([]);
 const [workOrders, setWorkOrders] = useState([]);
 const [selectedProject, setSelectedProject] = usePersistentState("commercial_dashboard_project", "");
 const [billingOverview, setBillingOverview] = useState(null);
 const [paymentsOverview, setPaymentsOverview] = useState(null);
 const [milestoneStats, setMilestoneStats] = useState(null);
 const [recentBillings, setRecentBillings] = useState([]);
 const [loading, setLoading] = useState(false);
 const [refreshing, setRefreshing] = useState(false);

 // Modal states
 const [showRecovery, setShowRecovery] = useState(false);
 const [showAdvance, setShowAdvance] = useState(false);
 const [showCostVsBudget, setShowCostVsBudget] = useState(false);

 // Fetch master lists 
 const fetchMaster = useCallback(async () => {
 try {
 const [projRes, woRes] = await Promise.all([
 api.get("/projects?limit=1000"),
 api.get("/work-orders?limit=1000"),
 ]);
 setProjects(projRes.data?.data || []);
 setWorkOrders(woRes.data?.data || []);
 } catch {
 /* silent */
 }
 }, []);

 // Fetch dashboard data 
 const fetchDashboardData = useCallback(async () => {
 setLoading(true);
 try {
 const projectParam = selectedProject ? `?project=${selectedProject}` : "";
 const projectParamAmp = selectedProject ? `&project=${selectedProject}` : "";

 const [billRes, payRes, recentRes] = await Promise.all([
 api
 .get(`/billing/overview${projectParam}`)
 .catch(() => ({ data: { data: null } })),
 selectedProject
 ? api
 .get(`/payments/project/${selectedProject}`)
 .catch(() => ({ data: null }))
 : api
 .get("/payments")
 .catch(() => ({ data: { data: [] } })),
 api
 .get(`/billing?limit=8${projectParamAmp}`)
 .catch(() => ({ data: { data: [] } })),
 ]);

 setBillingOverview(billRes.data?.data || null);

 if (selectedProject) {
 // Single project: response is the project payment document directly
 setPaymentsOverview(payRes.data || null);
 } else {
 // All projects: wrap array for aggregation
 setPaymentsOverview({ projects: payRes.data?.data || [] });
 }

 setRecentBillings(recentRes.data?.data || []);
 } catch {
 /* silent */
 } finally {
 setLoading(false);
 }
 }, [selectedProject]);

 // Fetch progress summary 
 const fetchProgress = useCallback(async () => {
 try {
 const res = await api.get("/milestones/stats", {
 params: selectedProject ? { project: selectedProject } : {},
 });
 setMilestoneStats(res.data?.data || null);
 } catch {
 setMilestoneStats(null);
 }
 }, [selectedProject]);

 // Fetch master lists once on mount
 useEffect(() => {
 fetchMaster();
 }, [fetchMaster]);

 // Re-fetch dashboard data + progress whenever selectedProject changes
 useEffect(() => {
 fetchDashboardData();
 fetchProgress();
 }, [fetchDashboardData, fetchProgress]);

 const handleRefresh = async () => {
 setRefreshing(true);
 await Promise.all([fetchDashboardData(), fetchProgress()]);
 setRefreshing(false);
 };

 // Derived values 
 const billing = billingOverview?.summary || {};
 const collectionPct =
 billing.totalAmount > 0
 ? Math.round((billing.paidAmount / billing.totalAmount) * 100)
 : 0;

 const paymentSummary = useMemo(() => {
 if (selectedProject && paymentsOverview && !paymentsOverview.projects) {
 // Single project response: summary lives inside .data or directly on the object
 return paymentsOverview.data?.summary || paymentsOverview.summary || {};
 }
 if (!selectedProject && paymentsOverview?.projects) {
 return paymentsOverview.projects.reduce(
 (acc, card) => ({
 totalValue: acc.totalValue + Number(card.summary?.totalValue || 0),
 totalPayableWithTax:
 acc.totalPayableWithTax + Number(card.summary?.totalPayableWithTax || 0),
 totalDeductionAmount:
 acc.totalDeductionAmount + Number(card.summary?.totalDeductionAmount || 0),
 }),
 { totalValue: 0, totalPayableWithTax: 0, totalDeductionAmount: 0 }
 );
 }
 return {};
 }, [selectedProject, paymentsOverview]);

 const milestoneTaskTotal = Math.max(
 Number(milestoneStats?.activeTotal ?? milestoneStats?.total ?? 0) -
 (milestoneStats?.activeTotal == null ? Number(milestoneStats?.cancelled || 0) : 0),
 0
 );
 const milestoneTaskCompleted = Number(milestoneStats?.completed || 0);

 const progressPct = useMemo(() => {
 if (!milestoneStats) return 0;
 return Number(milestoneStats.physicalProgressPct ?? milestoneStats.avgCompletionPct ?? 0);
 }, [milestoneStats]);

 // Chart data 
 const statusChartData = billingOverview?.statusBreakdown || [];
 const stageChartData = billingOverview?.stageBreakdown || [];
  const payDonutData = [
    { name: "Paid", value: Number(billing.paidAmount || 0) },
    ...(Number(billing.totalDeductions || 0) > 0
      ? [{ name: "Deductions", value: Number(billing.totalDeductions) }]
      : []),
    { name: "Pending", value: Number(billing.pendingAmount || 0) },
  ];

 if (loading) {
 return (
 <div className="text-center py-5">
 <Spinner animation="border" variant="primary" />
 <p className="text-muted mt-3 small">Loading commercial dashboard…</p>
 </div>
 );
 }

 return (
 <Fragment>
 {/* Header */}
 <div className="d-flex justify-content-between align-items-start mb-4 flex-wrap gap-3">
 <div>
 <h4 className="fw-bold mb-1">
 <i className="fas fa-chart-line text-primary me-2" />
 Commercial Dashboard
 </h4>
 <p className="text-muted mb-0 small">
 Financial progress monitoring, billing status &amp; recovery tracking
 </p>
 </div>
 <div className="d-flex gap-2 align-items-center flex-wrap">
 {/* Project Filter */}
 <div style={{ minWidth: 260 }}>
 <Form.Select
 size="sm"
 value={selectedProject}
 onChange={(e) => setSelectedProject(e.target.value)}
 >
 <option value="">All Projects</option>
 {projects.map((p) => (
 <option key={p._id} value={p._id}>
 {p.projectCode ? `${p.projectCode} - ` : ""}
 {p.projectName}
 </option>
 ))}
 </Form.Select>
 </div>
 <Button
 variant="light"
 size="sm"
 className="border"
 onClick={handleRefresh}
 disabled={refreshing}
 title="Refresh"
 >
 {refreshing ? (
 <Spinner animation="border" size="sm" />
 ) : (
 <i className="fas fa-sync-alt text-primary" />
 )}
 </Button>
 </div>
 </div>

 {/* Quick Action Buttons */}
 <Row className="g-2 mb-4">
 {[
 {
 label: "Recovery Statement",
 icon: "fa-file-invoice-dollar",
 color: "#28A745",
 onClick: () => setShowRecovery(true),
 desc: "View & export recovery status",
 },
 {
 label: "Advance Statement",
 icon: "fa-hand-holding-usd",
 color: "#F47920",
 onClick: () => setShowAdvance(true),
 desc: "Deductions & payment terms",
 },
 {
 label: "Cost vs Budget",
 icon: "fa-balance-scale",
 color: "#0078D7",
 onClick: () => setShowCostVsBudget(true),
 desc: "Budget utilisation analysis",
 },
 ].map((btn) => (
 <Col md={4} key={btn.label}>
 <div
 className="card border-0 shadow-sm h-100 cursor-pointer"
 style={{ borderLeft: `4px solid ${btn.color}`, cursor: "pointer" }}
 onClick={btn.onClick}
 role="button"
 tabIndex={0}
 onKeyDown={(e) => e.key === "Enter" && btn.onClick()}
 >
 <div className="card-body d-flex align-items-center gap-3 p-3">
 <div
 className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
 style={{ width: 48, height: 48, background: `${btn.color}18` }}
 >
 <i className={`fas ${btn.icon} fs-5`} style={{ color: btn.color }} />
 </div>
 <div>
 <h6 className="fw-bold mb-0">{btn.label}</h6>
 <small className="text-muted">{btn.desc}</small>
 </div>
 <i className="fas fa-chevron-right text-muted ms-auto" />
 </div>
 </div>
 </Col>
 ))}
 </Row>

 {/* KPI Stats */}
 <Row className="g-3 mb-4">
 <Col xl={3} sm={6}>
 <StatCard
 icon="fa-file-invoice"
 label="Total Invoices"
 value={billing.count || 0}
 color="#0078D7"
 />
 </Col>
 <Col xl={3} sm={6}>
 <StatCard
 icon="fa-rupee-sign"
 label="Total Billed"
 value={fmtCr(billing.totalAmount)}
 color="#F47920"
 />
 </Col>
 <Col xl={3} sm={6}>
 <StatCard
 icon="fa-check-circle"
 label="Amount Recovered"
 value={fmtCr(billing.paidAmount)}
 color="#28A745"
 sub={`${collectionPct}% collection rate`}
 />
 </Col>
 <Col xl={3} sm={6}>
 <StatCard
 icon="fa-clock"
 label="Pending Recovery"
 value={fmtCr(billing.pendingAmount)}
 color="#DC3545"
 />
 </Col>
 </Row>

 {/* Secondary Stats */}
 <Row className="g-3 mb-4">
 <Col xl={3} sm={6}>
 <StatCard
 icon="fa-briefcase"
 label="WO Contract Value"
 value={fmtCr(paymentSummary.totalValue)}
 color="#6F42C1"
 />
 </Col>
 <Col xl={3} sm={6}>
 <StatCard
 icon="fa-receipt"
 label="Net Receivable (with GST)"
 value={fmtCr(paymentSummary.totalPayableWithTax)}
 color="#17A2B8"
 />
 </Col>
 <Col xl={3} sm={6}>
 <StatCard
 icon="fa-minus-circle"
 label="Total Deductions"
 value={fmtCr(paymentSummary.totalDeductionAmount)}
 color="#E83E8C"
 />
 </Col>
 <Col xl={3} sm={6}>
 <StatCard
 icon="fa-hard-hat"
 label="Physical Progress"
 value={`${progressPct}%`}
 color={progressPct >= 90 ? "#28A745" : progressPct >= 60 ? "#FFC107" : "#DC3545"}
 sub={
 milestoneStats
 ? `${milestoneTaskCompleted} / ${milestoneTaskTotal} tasks completed`
 : "No milestone data"
 }
 />
 </Col>
 </Row>

 {/* Collection Progress Bar */}
 <Card className="border-0 shadow-sm mb-4">
 <Card.Body>
 <Row className="g-3 align-items-center">
 <Col md={6}>
 <div className="d-flex justify-content-between small mb-1">
 <span className="fw-semibold">Overall Collection Rate</span>
 <span className="fw-bold">{collectionPct}%</span>
 </div>
 <ProgressBar
 now={collectionPct}
 variant={
 collectionPct >= 90
 ? "success"
 : collectionPct >= 60
 ? "warning"
 : "danger"
 }
 style={{ height: 14, borderRadius: 8 }}
 />
 <small className="text-muted mt-1 d-block">
 {fmtCr(billing.paidAmount)} recovered of {fmtCr(billing.totalAmount)} billed
 </small>
 </Col>
 <Col md={6}>
 <div className="d-flex justify-content-between small mb-1">
 <span className="fw-semibold">Physical Progress</span>
 <span className="fw-bold">{progressPct}%</span>
 </div>
 <ProgressBar
 now={progressPct}
 variant={statusVariant(progressPct)}
 style={{ height: 14, borderRadius: 8 }}
 />
 <small className="text-muted mt-1 d-block">
 Completed milestones vs total milestone tasks
 </small>
 </Col>
 </Row>
 </Card.Body>
 </Card>

 {/* Charts Row */}
 <Row className="g-3 mb-4">
 {/* Billing Status Distribution */}
 <Col lg={4}>
 <Card className="border-0 shadow-sm h-100">
 <Card.Header className="bg-white border-0 fw-bold pt-3 pb-0">
 Billing Status Distribution
 </Card.Header>
 <Card.Body>
 {statusChartData.length === 0 ? (
 <div className="text-center py-4 text-muted small">No billing data</div>
 ) : (
 <ResponsiveContainer width="100%" height={260}>
 <PieChart>
 <Pie
 data={statusChartData}
 dataKey="value"
 nameKey="name"
 cx="50%"
 cy="50%"
 outerRadius={90}
 innerRadius={45}
 paddingAngle={4}
 >
 {statusChartData.map((_, i) => (
 <Cell key={i} fill={BRAND_COLORS[i % BRAND_COLORS.length]} />
 ))}
 </Pie>
 <Tooltip
 formatter={(v) => [v, "Invoices"]}
 contentStyle={{ borderRadius: 8, fontSize: 12 }}
 />
 <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
 </PieChart>
 </ResponsiveContainer>
 )}
 </Card.Body>
 </Card>
 </Col>

 {/* Payment Health */}
 <Col lg={4}>
 <Card className="border-0 shadow-sm h-100">
 <Card.Header className="bg-white border-0 fw-bold pt-3 pb-0 d-flex justify-content-between align-items-center">
 <span>Payment Health</span>
 <span
 className={`badge bg-light border fw-semibold ${
 collectionPct >= 90
 ? "text-success border-success"
 : collectionPct >= 60
 ? "text-warning border-warning"
 : "text-danger border-danger"
 }`}
 style={{ fontSize: "11px" }}
 >
 Collection: {collectionPct}%
 </span>
 </Card.Header>
 <Card.Body>
 {payDonutData.every((d) => d.value === 0) ? (
 <div className="text-center py-4 text-muted small">No payment data</div>
 ) : (
 <ResponsiveContainer width="100%" height={260}>
 <PieChart>
 <Pie
 data={payDonutData.filter((d) => d.value > 0)}
 dataKey="value"
 nameKey="name"
 cx="50%"
 cy="50%"
 innerRadius={45}
 outerRadius={90}
 paddingAngle={4}
 >
 {payDonutData
 .filter((d) => d.value > 0)
 .map((entry) => (
 <Cell
 key={entry.name}
 fill={
 entry.name === "Paid"
 ? "#28A745"
 : entry.name === "Deductions"
 ? "#F47920"
 : "#DC3545"
 }
 />
 ))}
 </Pie>
 <Tooltip
 formatter={(v) => [fmt(v), "Amount"]}
 contentStyle={{ borderRadius: 8, fontSize: 12 }}
 />
 <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
 </PieChart>
 </ResponsiveContainer>
 )}
 </Card.Body>
 </Card>
 </Col>

 {/* Stage Distribution */}
 <Col lg={4}>
 <Card className="border-0 shadow-sm h-100">
 <Card.Header className="bg-white border-0 fw-bold pt-3 pb-0">
 Stage Distribution
 </Card.Header>
 <Card.Body>
 {stageChartData.length === 0 ? (
 <div className="text-center py-4 text-muted small">No stage data</div>
 ) : (
 <ResponsiveContainer width="100%" height={260}>
 <BarChart
 data={stageChartData}
 margin={{ top: 5, right: 10, left: 0, bottom: 30 }}
 barCategoryGap="30%"
 >
 <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
 <XAxis
 dataKey="name"
 tick={{ fontSize: 10 }}
 angle={-35}
 textAnchor="end"
 interval={0}
 />
 <YAxis tick={{ fontSize: 10 }} />
 <Tooltip
 contentStyle={{ borderRadius: 8, fontSize: 12 }}
 formatter={(v) => [v, "Invoices"]}
 />
 <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={40}>
 {stageChartData.map((_, i) => (
 <Cell key={i} fill={BRAND_COLORS[i % BRAND_COLORS.length]} />
 ))}
 </Bar>
 </BarChart>
 </ResponsiveContainer>
 )}
 </Card.Body>
 </Card>
 </Col>
 </Row>

 {/* Recent Invoices Table */}
 <Card className="border-0 shadow-sm mb-4">
 <Card.Header className="bg-white border-bottom d-flex justify-content-between align-items-center py-3">
 <SectionHeader icon="fa-file-invoice" title="Recent Invoices" />
 <a href="/billings" className="btn btn-outline-primary btn-sm">
 View All <i className="fas fa-arrow-right ms-1" />
 </a>
 </Card.Header>
 <Card.Body className="p-0">
 {recentBillings.length === 0 ? (
 <div className="text-center py-5 text-muted">
 <i className="fas fa-file-invoice fs-2 mb-2 d-block opacity-25" />
 <p className="small mb-0">No recent invoices</p>
 </div>
 ) : (
 <div className="table-responsive">
 <Table hover className="mb-0 align-middle" style={{ fontSize: 13 }}>
 <thead className="table-light">
 <tr>
 <th className="ps-3">Invoice No</th>
 <th>Work Order</th>
 <th>Status</th>
 <th className="text-end">Total</th>
 <th className="text-end">Paid</th>
 <th className="text-end">Pending</th>
 <th>Collection %</th>
 <th>Date</th>
 </tr>
 </thead>
 <tbody>
 {recentBillings.map((b) => {
 const paid = Number(b.paidAmount || 0);
 const total = Number(b.totalAmount || 0);
 const pending = Math.max(total - paid, 0);
 const cp = pct(paid, total);
 const woObj =
 typeof b.workOrder === "object"
 ? b.workOrder
 : workOrders.find((w) => w._id === b.workOrder);
 return (
 <tr key={b._id}>
 <td className="ps-3">
 <strong className="text-primary">{b.invoiceNumber}</strong>
 </td>
 <td>{woObj?.woNo || woObj?.woNumber || "—"}</td>
 <td>
 <Badge bg={billingBadge(b.status)} style={{ fontSize: 10 }}>
 {b.status}
 </Badge>
 </td>
 <td className="text-end">{fmt(total)}</td>
 <td className="text-end text-success fw-semibold">{fmt(paid)}</td>
 <td
 className={`text-end fw-semibold ${
 pending > 0 ? "text-danger" : "text-muted"
 }`}
 >
 {fmt(pending)}
 </td>
 <td>
 <div className="d-flex align-items-center gap-2">
 <ProgressBar
 now={cp}
 variant={statusVariant(cp)}
 style={{ flex: 1, height: 6, borderRadius: 4 }}
 />
 <span style={{ width: 36, fontSize: 11 }}>{cp}%</span>
 </div>
 </td>
 <td className="text-muted">
 {fmtDate(b.createdAt)}
 </td>
 </tr>
 );
 })}
 </tbody>
 </Table>
 </div>
 )}
 </Card.Body>
 </Card>

 {/* Modals */}
 <RecoveryStatementModal
 show={showRecovery}
 onHide={() => setShowRecovery(false)}
 projectId={selectedProject}
 projects={projects}
 workOrders={workOrders}
 />
 <AdvanceStatementModal
 show={showAdvance}
 onHide={() => setShowAdvance(false)}
 projectId={selectedProject}
 projects={projects}
 workOrders={workOrders}
 />
 <CostVsBudgetModal
 show={showCostVsBudget}
 onHide={() => setShowCostVsBudget(false)}
 projectId={selectedProject}
 projects={projects}
 workOrders={workOrders}
 />
 </Fragment>
 );
};

export default CommercialDashboard;
